/**
 * Build a branded 1080×1920 "Instagram Story" card for a Willoah seed as a
 * PNG Blob. Runs entirely on-canvas in the client — no server round-trip —
 * so it works for anon viewers too.
 *
 * Layout (top to bottom):
 *   1. Cream background (matches Willoah brand)
 *   2. Rounded photo (top ~55% of canvas) — falls back to a soft gradient
 *      panel if the seed has no photo or the image fails to load.
 *   3. Category eyebrow (small brass caps)
 *   4. Serif title, up to 3 lines with ellipsis
 *   5. Author byline in italic
 *   6. Willoah wordmark + willoah.com CTA at the bottom
 */
import { FILE_URL } from "./api";

const W = 1080;
const H = 1920;

// Willoah palette — keep in sync with tailwind theme.
const CREAM = "#FBF8F1";
const OLIVE = "#4E5A3D";
const BRASS = "#B79256";
const CHARCOAL = "#2A2823";

// Best-effort image load with anonymous CORS so canvas stays exportable.
// Falls back to null if the image can't be reached — the card will render
// the gradient panel instead.
function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// Draw a rounded-rectangle path on the current context. Native
// ctx.roundRect isn't shipped in every browser we care about (Safari 15).
function roundedRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

// Wrap text to a given pixel width, up to `maxLines`; the last visible
// line gets an ellipsis if content overflows.
function wrapLines(ctx, text, maxWidth, maxLines) {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  for (const word of words) {
    const trial = cur ? `${cur} ${word}` : word;
    if (ctx.measureText(trial).width > maxWidth && cur) {
      lines.push(cur);
      cur = word;
      if (lines.length === maxLines) break;
    } else {
      cur = trial;
    }
  }
  if (cur && lines.length < maxLines) lines.push(cur);
  if (lines.length === maxLines) {
    const last = lines[maxLines - 1];
    if (ctx.measureText(last).width > maxWidth || words.join(" ") !== lines.join(" ")) {
      let trimmed = last;
      while (trimmed && ctx.measureText(trimmed + "…").width > maxWidth) {
        trimmed = trimmed.slice(0, -1);
      }
      lines[maxLines - 1] = (trimmed || "").trimEnd() + "…";
    }
  }
  return lines;
}

// Cover-fit an image inside a target box (behaves like `background-size: cover`).
function drawCover(ctx, img, dx, dy, dw, dh) {
  const srcRatio = img.width / img.height;
  const dstRatio = dw / dh;
  let sx, sy, sw, sh;
  if (srcRatio > dstRatio) {
    // source is wider → crop sides
    sh = img.height;
    sw = sh * dstRatio;
    sx = (img.width - sw) / 2;
    sy = 0;
  } else {
    sw = img.width;
    sh = sw / dstRatio;
    sx = 0;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

export async function buildInstagramStoryCard(idea) {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas-unsupported");

  // Cream background
  ctx.fillStyle = CREAM;
  ctx.fillRect(0, 0, W, H);

  // ---- Photo panel (top) ----
  const PAD = 72;
  const photoX = PAD;
  const photoY = PAD;
  const photoW = W - PAD * 2;
  const photoH = Math.round(H * 0.55);

  ctx.save();
  roundedRect(ctx, photoX, photoY, photoW, photoH, 48);
  ctx.clip();

  const photoSrc = idea?.photo_path ? FILE_URL(idea.photo_path) : null;
  const img = await loadImage(photoSrc);
  if (img) {
    drawCover(ctx, img, photoX, photoY, photoW, photoH);
  } else {
    // Warm sage → brass gradient fallback so the card still feels branded.
    const grad = ctx.createLinearGradient(photoX, photoY, photoX + photoW, photoY + photoH);
    grad.addColorStop(0, OLIVE);
    grad.addColorStop(1, BRASS);
    ctx.fillStyle = grad;
    ctx.fillRect(photoX, photoY, photoW, photoH);
    // Small sprout mark in the middle of the fallback.
    ctx.fillStyle = "rgba(251,248,241,0.85)";
    ctx.font = "italic 88px 'Playfair Display', Georgia, serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Willoah", photoX + photoW / 2, photoY + photoH / 2);
  }
  ctx.restore();

  // ---- Category eyebrow ----
  const textLeft = PAD;
  const contentTop = photoY + photoH + 88;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = BRASS;
  ctx.font = "600 26px Inter, system-ui, sans-serif";
  const eyebrow = (idea?.category || "REAL LIFE").toUpperCase();
  // Poor-man's letter-spacing: draw char-by-char with fixed gap
  const drawTracked = (text, x, y, spacing) => {
    let cx = x;
    for (const ch of text) {
      ctx.fillText(ch, cx, y);
      cx += ctx.measureText(ch).width + spacing;
    }
  };
  drawTracked(eyebrow, textLeft, contentTop, 6);

  // ---- Title ----
  ctx.fillStyle = CHARCOAL;
  ctx.font = "72px 'Playfair Display', Georgia, serif";
  const titleLines = wrapLines(ctx, idea?.title || "A real idea, well shared.", W - PAD * 2, 3);
  let titleY = contentTop + 66;
  for (const line of titleLines) {
    ctx.fillText(line, textLeft, titleY);
    titleY += 90;
  }

  // ---- Author byline ----
  ctx.fillStyle = OLIVE;
  ctx.font = "italic 34px 'Playfair Display', Georgia, serif";
  const author = idea?.author_name ? `by ${idea.author_name}` : "on Willoah";
  ctx.fillText(author, textLeft, titleY + 12);

  // ---- Footer wordmark + url ----
  const footerY = H - 120;
  ctx.fillStyle = CHARCOAL;
  ctx.textAlign = "left";
  ctx.font = "italic 56px 'Playfair Display', Georgia, serif";
  ctx.fillText("Willoah", textLeft, footerY);
  ctx.fillStyle = BRASS;
  ctx.font = "600 26px Inter, system-ui, sans-serif";
  drawTracked("REAL LIFE.  REAL IDEAS.", textLeft, footerY + 44, 4);

  // Right side — the URL
  ctx.textAlign = "right";
  ctx.fillStyle = OLIVE;
  ctx.font = "28px Inter, system-ui, sans-serif";
  ctx.fillText("willoah.com", W - PAD, footerY);

  // Convert to PNG blob
  return await new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error("blob-failed"));
      resolve(blob);
    }, "image/png", 0.95);
  });
}
